/**
 * The stylesheet the primitives expect, in one string.
 *
 * Every rule reads from `tokens.ts` and none of them names a colour. Injected once by each app
 * beside the tokens; see `main.tsx`.
 *
 * Mobile first, and that is a structural claim rather than a media-query habit: the layout is a
 * single column because figure 5 is a single column on a phone, which is where the dominant action -
 * photographing a driver's licence - actually happens. D-15 makes every surface mobile first, and
 * section 19 says to build this one on a phone rather than on a laptop with the viewport narrowed.
 * The `min-width` rules below are all additive: they widen a layout that already works.
 *
 * ## Three containers, one system
 *
 * `.ddds-page` is a prospect's page - narrow, centred, thumb room at the bottom. `.ddds-embedded` is
 * the widget, which is handed a panel by a CRM and has to fill it. `.ddds-app` is the standalone
 * application, which is an application and has a rail. They share every primitive below; only the
 * geometry differs, which is the point of a design system rather than three stylesheets.
 */
export const surfaceStyles = `
  * { box-sizing: border-box; }

  body {
    margin: 0;
    font-family: var(--ddds-font);
    line-height: 1.55;
    background: var(--ddds-bg);
    color: var(--ddds-ink);
    /* Stops iOS inflating text in landscape, which breaks a fixed-width code field. */
    -webkit-text-size-adjust: 100%;
  }

  .ddds-page {
    max-width: 34rem;
    margin: 0 auto;
    /* Generous on a phone and not cramped against the notch. */
    padding: 1.5rem 1.25rem 4rem;
  }
  @media (min-width: 40rem) { .ddds-page { padding-top: 3rem; } }

  .ddds-eyebrow {
    font-family: var(--ddds-font-mono);
    font-size: 0.68rem;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--ddds-accent);
    margin: 0 0 0.7rem;
  }

  /*
   * Headings are the display face; everything else is the body face.
   *
   * Bricolage has noticeably tighter sidebearings than Albert Sans, so the negative tracking that
   * makes a heading look set rather than typed is smaller here than it would be for a neutral
   * grotesque. Do not copy this value onto body text.
   */
  .ddds-title {
    font-family: var(--ddds-font-display);
    font-weight: 600;
    font-size: clamp(1.35rem, 5vw, 1.7rem);
    line-height: 1.15;
    letter-spacing: -0.02em;
    text-wrap: balance;
    margin: 0 0 0.4rem;
  }

  .ddds-lede { margin: 0 0 var(--ddds-gap-lg); color: var(--ddds-ink-quiet); }

  /*
   * Links, which were default browser blue until somebody looked at the rendered page. On a surface
   * whose accent is a customer's, an unstyled link is the one element that ignores the theme - and a
   * document name is the most-clicked thing on the exchange view.
   *
   * Underlined, with the offset backed off. The underline is what makes it read as a link at all once
   * the colour is no longer the familiar blue.
   */
  .ddds-page a, .ddds-embedded a, .ddds-app a {
    color: var(--ddds-accent);
    text-decoration: underline;
    text-underline-offset: 0.15em;
    text-decoration-thickness: 1px;
  }
  .ddds-page a:hover, .ddds-embedded a:hover, .ddds-app a:hover { text-decoration-thickness: 2px; }
  .ddds-page a:focus-visible, .ddds-embedded a:focus-visible, .ddds-app a:focus-visible {
    outline: 2px solid var(--ddds-accent);
    outline-offset: 2px;
    border-radius: 2px;
  }

  .ddds-card {
    background: var(--ddds-surface);
    border: 1px solid var(--ddds-line);
    border-radius: var(--ddds-radius);
    box-shadow: var(--ddds-shadow);
    padding: var(--ddds-gap);
    margin: 0 0 var(--ddds-gap);
  }
  .ddds-card__title {
    font-family: var(--ddds-font-display);
    font-weight: 600;
    font-size: 0.98rem;
    letter-spacing: -0.01em;
    margin: 0 0 0.75rem;
  }

  /* --- Buttons ------------------------------------------------------------ */

  .ddds-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    min-height: var(--ddds-touch);
    width: 100%;
    padding: 0.65rem 1.1rem;
    border: 1px solid transparent;
    border-radius: var(--ddds-radius-sm);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    background: var(--ddds-accent);
    color: var(--ddds-accent-ink);
  }
  .ddds-button:hover:not(:disabled) { background: var(--ddds-accent-hover); }
  .ddds-button--quiet {
    background: var(--ddds-surface);
    color: var(--ddds-accent);
    border-color: var(--ddds-line-strong);
    font-weight: 500;
  }
  .ddds-button--quiet:hover:not(:disabled) {
    background: var(--ddds-accent-quiet);
    border-color: var(--ddds-accent);
  }
  /*
   * "Small" means narrow and quieter, never shorter than a thumb.
   *
   * This carried min-height 0, which made the Upload button 36px tall - under the touch token - and
   * the browser check caught it. It is the most important tap on the whole surface, made one-handed
   * and possibly outdoors, so the variant now reduces width, padding and type size and leaves the
   * height alone. A 44px button with small text still reads as secondary; a 36px one is just harder
   * to hit.
   *
   * No backticks in comments in this file: it is one template literal, and a backtick ends it.
   */
  .ddds-button--small { width: auto; padding: 0.4rem 0.9rem; font-size: 0.85rem; }
  /* Full width is the phone default; a header action sitting beside another one is not. */
  .ddds-button--auto { width: auto; }
  .ddds-button:disabled { opacity: 0.55; cursor: not-allowed; }
  /* Keyboard focus stays visible. A prospect may be on a tablet with a keyboard. */
  .ddds-button:focus-visible, .ddds-field__input:focus-visible {
    outline: 2px solid var(--ddds-accent);
    outline-offset: 2px;
  }

  /* --- Fields ------------------------------------------------------------- */

  .ddds-field { display: block; margin: 0 0 var(--ddds-gap); }
  .ddds-field__label { display: block; font-weight: 600; margin: 0 0 0.3rem; font-size: 0.92rem; }
  .ddds-field__hint { display: block; color: var(--ddds-ink-quiet); font-size: 0.83rem; margin: 0 0 0.45rem; }
  .ddds-field__input {
    width: 100%;
    min-height: var(--ddds-touch);
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--ddds-line-strong);
    border-radius: var(--ddds-radius-sm);
    background: var(--ddds-surface);
    color: var(--ddds-ink);
    /* 16px minimum, or mobile Safari zooms the whole page on focus. */
    font: inherit;
    font-size: 1rem;
  }
  .ddds-field__input--code {
    font-family: var(--ddds-font-mono);
    font-size: 1.5rem;
    letter-spacing: 0.4em;
    text-align: center;
  }
  .ddds-field__error { display: block; color: var(--ddds-error-ink); font-size: 0.875rem; margin: 0.4rem 0 0; }

  /* --- Rich Text Editor --------------------------------------------------- */

  .ddds-rich-editor {
    display: block;
    margin: 0 0 var(--ddds-gap);
  }
  .ddds-rich-editor__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.35rem 0.5rem;
    background: var(--ddds-sunken);
    border: 1px solid var(--ddds-line-strong);
    border-bottom: none;
    border-radius: var(--ddds-radius-sm) var(--ddds-radius-sm) 0 0;
  }
  .ddds-rich-editor__tool-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 1.85rem;
    padding: 0 0.4rem;
    border: 1px solid var(--ddds-line);
    border-radius: var(--ddds-radius-sm);
    background: var(--ddds-surface);
    color: var(--ddds-ink);
    font: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }
  .ddds-rich-editor__tool-btn:hover {
    background: var(--ddds-sunken);
  }
  .ddds-rich-editor__tool-btn:focus-visible {
    outline: 2px solid var(--ddds-accent);
    outline-offset: 1px;
  }
  .ddds-rich-editor__content {
    min-height: 5.5rem;
    max-height: 14rem;
    overflow-y: auto;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--ddds-line-strong);
    border-radius: 0 0 var(--ddds-radius-sm) var(--ddds-radius-sm);
    background: var(--ddds-surface);
    color: var(--ddds-ink);
    font: inherit;
    font-size: 0.95rem;
    line-height: 1.5;
  }
  .ddds-rich-editor__content:focus-visible {
    outline: 2px solid var(--ddds-accent);
    outline-offset: 2px;
  }
  .ddds-rich-editor__content p { margin: 0 0 0.5rem; }
  .ddds-rich-editor__content p:last-child { margin-bottom: 0; }
  .ddds-rich-editor__content ul, .ddds-rich-editor__content ol { margin: 0 0 0.5rem; padding-left: 1.25rem; }
  .ddds-rich-editor__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.35rem;
    font-size: 0.8rem;
    color: var(--ddds-ink-quiet);
  }
  .ddds-rich-editor__counter--exceeded {
    color: var(--ddds-error-ink);
    font-weight: 600;
  }
  /* --- Status ------------------------------------------------------------- */

  .ddds-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.12rem 0.6rem;
    border-radius: var(--ddds-radius-pill);
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .ddds-pill--done { background: var(--ddds-ok-quiet); color: var(--ddds-ok); }
  .ddds-pill--waiting { background: var(--ddds-warn-quiet); color: var(--ddds-warn-ink); }
  .ddds-pill--quiet { background: var(--ddds-sunken); color: var(--ddds-ink-quiet); }

  .ddds-notice {
    border-left: 3px solid var(--ddds-warn-line);
    background: var(--ddds-warn-quiet);
    color: var(--ddds-warn-ink);
    padding: 0.7rem 0.9rem;
    border-radius: 0 var(--ddds-radius-sm) var(--ddds-radius-sm) 0;
    margin: 0 0 var(--ddds-gap);
  }
  .ddds-notice--error {
    border-left-color: var(--ddds-error-ink);
    background: var(--ddds-error-quiet);
    color: var(--ddds-error-ink);
  }
  /*
   * The correlation id, shown with every failure. Section 13: it is the only thing a person can
   * read back to us over the phone, so it is selectable and monospaced rather than hidden in a
   * console nobody on a phone can open.
   */
  .ddds-notice__reference {
    display: block;
    font-family: var(--ddds-font-mono);
    font-size: 0.73rem;
    margin-top: 0.35rem;
    opacity: 0.85;
    user-select: all;
  }

  /* --- Lists -------------------------------------------------------------- */

  .ddds-list { list-style: none; margin: 0; padding: 0; }
  .ddds-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.7rem 0;
    border-top: 1px solid var(--ddds-line);
  }
  .ddds-row:first-child { border-top: 0; }
  /* Grows and truncates: a prospect's filename can be very long and must not push the action off. */
  .ddds-row__main { flex: 1 1 auto; min-width: 0; }
  .ddds-row__name { display: block; overflow-wrap: anywhere; font-weight: 500; }
  .ddds-row__meta { display: block; color: var(--ddds-ink-quiet); font-size: 0.83rem; }
  .ddds-row__action { flex: 0 0 auto; }
  .ddds-row__actions { display: flex; align-items: center; gap: 0.5rem; }
  .ddds-request-actions { display: flex; flex-direction: column; align-items: flex-start; gap: 0.5rem; margin-top: 0.65rem; }
  .ddds-formatted-request p, .ddds-formatted-request div { margin: 0 0 0.4rem; }
  .ddds-formatted-request p:last-child, .ddds-formatted-request div:last-child { margin-bottom: 0; }
  .ddds-formatted-request ul, .ddds-formatted-request ol { margin: 0 0 0.4rem; padding-left: 1.15rem; }
  .ddds-formatted-request a { color: var(--ddds-accent); text-decoration: underline; }
  .ddds-selected-files { list-style: none; width: 100%; margin: 0; padding: 0; }
  .ddds-selected-files li { display: flex; align-items: flex-start; gap: 0.5rem; justify-content: space-between; font-size: 0.83rem; font-weight: 400; }
  .ddds-selected-files li + li { margin-top: 0.35rem; }
  .ddds-selected-files span { min-width: 0; overflow-wrap: anywhere; }

  /*
    A native progress element, stripped back to the same bar it used to be a div for.

    appearance:none removes the platform widget, which otherwise draws its own inset track and ignores
    most of what follows. display:block matters too: a progress element is inline by default, which
    would leave it sitting on a text baseline with descender space under it.
  */
  .ddds-progress {
    appearance: none;
    display: block;
    border: 0;
    width: 100%;
    height: 6px;
    border-radius: var(--ddds-radius-pill);
    background: var(--ddds-line);
    overflow: hidden;
    margin-top: 0.5rem;
  }
  /*
    The track and the filled portion, which have to be written twice.

    Blink and WebKit split them - ::-webkit-progress-bar is the track, ::-webkit-progress-value is the
    fill - while Gecko exposes only ::-moz-progress-bar, which is the fill. They cannot share one rule:
    a selector list containing a pseudo-element the engine does not recognise is discarded whole, so a
    combined rule would style neither.
  */
  .ddds-progress::-webkit-progress-bar { background: var(--ddds-line); border-radius: var(--ddds-radius-pill); }
  .ddds-progress::-webkit-progress-value {
    background: var(--ddds-accent);
    border-radius: var(--ddds-radius-pill);
    transition: width 120ms linear;
  }
  .ddds-progress::-moz-progress-bar { background: var(--ddds-accent); border-radius: var(--ddds-radius-pill); }

  .ddds-footer {
    margin-top: var(--ddds-gap-lg);
    color: var(--ddds-ink-quiet);
    font-size: 0.85rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
  }

  /* Respect a person who has asked for less motion. */
  @media (prefers-reduced-motion: reduce) {
    .ddds-progress::-webkit-progress-value { transition: none; }
  }

  /* ---------------------------------------------------------------------
     The rep surfaces (stage 8)
     ---------------------------------------------------------------------
     The panels in components/src/rep otherwise reuse what the prospect
     surface already had, which is the point of a design system rather than
     a second stylesheet: a rep and a prospect looking at the same exchange
     should be looking at one product.
  */
  /*
    An embedded panel is not a page, which is the whole of S8.7.

    .ddds-page is a phone page: 34rem, centred, with 4rem of bottom padding so a thumb is not against
    the notch. That is right for a prospect who opened a link and wrong for a widget, which is given a
    panel by the CRM and has to fill it - and the first browser run showed exactly that, a narrow column
    floating in the middle of a 1100px frame with the two-column rule never engaging because the *inner*
    container was the narrow one.

    So the widget composes .ddds-embedded instead. Same primitives, same tokens, different container:
    full width, tighter padding, and no bottom gutter because the host page owns the space below.
  */
  .ddds-embedded {
    max-width: none;
    margin: 0;
    padding: 1rem 1rem 1.25rem;
  }

  /* The standalone application's one strip of chrome, used on the signed-out page. */
  .ddds-rep-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: var(--ddds-gap);
  }
  .ddds-rep-nav .ddds-eyebrow { margin: 0; }

  .ddds-page--wide { max-width: 64rem; }

  /*
    The contact header, which carries the two primary actions.

    DG.18a: this screen used to offer asking, inviting and sending as three peers, which is three
    decisions where a representative has one intention. The two actions live here, above the panels,
    and everything else on the screen is a consequence of one of them.

    Wraps rather than shrinks. Below the breakpoint the actions drop onto their own line at full
    width, because two half-width buttons at 390px produce two lines of wrapped label each.
  */
  .ddds-rep-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.9rem;
    margin-bottom: var(--ddds-gap);
  }
  .ddds-rep-header__who { display: flex; gap: 0.7rem; align-items: center; min-width: 0; }
  .ddds-rep-header .ddds-title { margin-bottom: 0.1rem; }
  .ddds-rep-header__meta {
    display: block;
    color: var(--ddds-ink-quiet);
    font-size: 0.88rem;
    overflow-wrap: anywhere;
  }
  .ddds-rep-header__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; width: 100%; }
  .ddds-rep-header__actions .ddds-button { flex: 1 1 auto; }
  @media (min-width: 48rem) {
    .ddds-rep-header__actions { margin-left: auto; width: auto; }
    .ddds-rep-header__actions .ddds-button { flex: 0 0 auto; }
  }

  .ddds-rep-add { margin-top: var(--ddds-gap); border-top: 1px solid var(--ddds-line); padding-top: var(--ddds-gap); }

  .ddds-rep-tools { display: grid; grid-template-columns: minmax(0, 1fr); gap: 1rem; max-width: none; background: var(--ddds-bg); }
  .ddds-rep-tools__panel { min-width: 0; }
  .ddds-rep-tools__panel > h1 { font-family: var(--ddds-font-display); font-size: 1.15rem; margin: 0 0 0.35rem; }
  .ddds-rep-tools__panel > .ddds-card { margin-top: 0.85rem; }
  .ddds-rep-tools__card-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
  .ddds-rep-tools__card-heading h2, .ddds-rep-tools__panel > .ddds-card > h2 { font-family: var(--ddds-font-display); font-size: 1rem; margin: 0 0 0.35rem; }
  .ddds-rep-tools__lede { color: var(--ddds-ink-quiet); font-size: 0.83rem; margin: 0 0 0.8rem; }
  .ddds-rep-tools__actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .ddds-submission-table { margin-top: 1rem; }
  .ddds-submission-table__head, .ddds-submission__row { display: grid; grid-template-columns: auto minmax(7rem, 1fr) minmax(9rem, 1.3fr) minmax(7rem, 1fr) auto auto; align-items: center; gap: 0.75rem; }
  .ddds-submission-table__head { padding: 0 0.75rem 0.55rem; color: var(--ddds-ink-quiet); font-family: var(--ddds-font-mono); font-size: 0.65rem; letter-spacing: 0.08em; text-transform: uppercase; }
  .ddds-submission__row { padding: 0.65rem 0.75rem; border-top: 1px solid var(--ddds-line); font-size: 0.86rem; }
  .ddds-submission__email { overflow-wrap: anywhere; }
  .ddds-submission__documents { margin: 0 0.75rem 0.5rem 1.5rem; padding: 0.5rem 0 0.25rem 1rem; border-left: 2px solid var(--ddds-accent-quiet); }
  .ddds-submission__document { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.45rem 0; font-size: 0.85rem; }
  .ddds-submission-associate { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.5rem; margin-top: 1rem; }
  .ddds-submission-associate .ddds-field { flex: 1 1 14rem; margin: 0; }
  @media (min-width: 58rem) { .ddds-rep-tools { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); align-items: stretch; } }
  @media (max-width: 42rem) { .ddds-submission-table__head { display: none; } .ddds-submission__row { grid-template-columns: auto 1fr auto; } .ddds-submission__row > :not(input):not(.ddds-button) { grid-column: 2; } .ddds-submission__email { color: var(--ddds-ink-quiet); font-size: 0.78rem; } .ddds-submission__row > input { grid-column: 1; grid-row: 1 / span 4; } .ddds-submission__row > .ddds-button { grid-column: 3; grid-row: 1 / span 4; } }

  /* Muted, and not italic: this is ordinary copy about an empty list rather than an aside. */
  /* display:block because this is also worn by an <output>, which is inline by default. */
  .ddds-empty { display: block; color: var(--ddds-ink-quiet); margin: 0; }

  /* ---------------------------------------------------------------------
     The tab strip (DG.41)
     ---------------------------------------------------------------------
     What replaced the two-column layout, and replaced rather than balanced
     it: .ddds-rep-columns put the checklist and both document lists in a
     3fr column with the trail alone in a 2fr one, which is four cards
     competing for one screen with an asymmetry nobody chose. DG.41's answer
     is one panel at a time, so the columns are gone rather than retuned.

     Written in .ddds-rail's shape above, and for the same reason. Horizontal
     is the unconditional base rule and the column is added inside the 60rem
     query, so the vertical form a host asks for falls back to a strip on a
     narrow viewport with no JavaScript at all - which is what lets Tabs.tsx
     treat orientation as a preference. It cannot know how wide it is: the
     widget is handed a pane by a CRM, and there is no viewport check anywhere
     in this frontend by policy.
  */
  .ddds-tabs { display: block; }

  /*
    It scrolls on a phone rather than wrapping, which is the rail's call above and made for the rail's
    reason: a rep on a phone should not lose a third of the screen to navigation.

    Measured, because this is close: the four tabs with their counts come to 421px and a 390px phone has
    358px of content width, so the fourth is clipped to a sliver and something has to give. Wrapping would
    show all four and cost a second 44px row, which is the trade the rail already declined. Tightening the
    padding and the gap bought 24px of it and there is no more to buy without a target under the touch
    token or a label nobody can read.

    Nobody has to swipe, at least: Tabs.tsx moves the selection with the arrow keys and focuses the button
    it moved to, and a browser scrolls a focused control into view.
  */
  .ddds-tabs__strip {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 0.15rem;
    overflow-x: auto;
    border-bottom: 1px solid var(--ddds-line);
    margin-bottom: var(--ddds-gap);
  }

  /*
    min-height is the touch token and stays it, whatever the type size says.

    See .ddds-button--small: that variant once carried min-height 0 and produced a 36px target on the
    most important tap on the surface. A tab is a smaller, quieter control than a button and it is still
    the thing a rep hits four times a minute, one-handed, on the phone D-15 puts them on.
  */
  .ddds-tabs__tab {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-height: var(--ddds-touch);
    padding: 0.45rem 0.65rem;
    border: 0;
    border-bottom: 2px solid transparent;
    border-radius: var(--ddds-radius-sm) var(--ddds-radius-sm) 0 0;
    background: transparent;
    font: inherit;
    font-size: 0.9rem;
    color: var(--ddds-ink-quiet);
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    flex: none;
  }
  .ddds-tabs__tab:hover { color: var(--ddds-ink); background: var(--ddds-accent-quiet); }
  /* Tight against its neighbours, so the ring sits closer than the 2px a standalone button gets. */
  .ddds-tabs__tab:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 1px; }

  /*
    Selected, off the ARIA attribute rather than a class the component would have to remember to add.

    The same trick as .ddds-rail__item[aria-current="page"] and .ddds-theme__option[aria-pressed="true"]:
    the attribute is what a screen reader reads, so styling from it means the two cannot disagree.

    Weight and a rule as well as colour, never colour alone. A rep with a colour vision deficiency, or on
    a customer theme whose accent is close to the ink, has to be able to see which tab they are on.
  */
  .ddds-tabs__tab[aria-selected="true"] {
    color: var(--ddds-ink);
    font-weight: 600;
    background: var(--ddds-surface);
    border-bottom-color: var(--ddds-accent);
  }

  /* A count beside the label. Tabular figures so the strip does not shift as one goes from 9 to 10. */
  .ddds-tabs__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.3rem;
    padding: 0 0.3rem;
    border-radius: var(--ddds-radius-pill);
    background: var(--ddds-sunken);
    color: var(--ddds-ink-quiet);
    font-size: 0.72rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .ddds-tabs__tab[aria-selected="true"] .ddds-tabs__count {
    background: var(--ddds-accent-quiet);
    color: var(--ddds-accent);
  }

  /*
    Something changed here while the rep was looking elsewhere.

    A colour and a shape, which is not a state: Tabs.tsx pairs it with a visually hidden word, and this is
    aria-hidden. Do not make it the only carrier of anything.
  */
  .ddds-tabs__dot {
    display: block;
    width: 6px;
    height: 6px;
    flex: none;
    border-radius: var(--ddds-radius-pill);
    background: var(--ddds-accent);
  }

  /* Focusable, so Tab out of the strip reaches the panel it just selected. It needs a ring for that. */
  .ddds-tabs__panel { min-width: 0; }
  .ddds-tabs__panel:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 2px; }

  /*
    The vertical form, which exists only where there is width for it.

    A column of tabs costs 12rem the panel does not control, so this is inside the query and the base rule
    above is the fallback: a host that asks for vertical gets a horizontal strip on a phone and in a narrow
    CRM pane, and nothing in JavaScript has to notice.
  */
  @media (min-width: 60rem) {
    .ddds-tabs--vertical {
      display: grid;
      grid-template-columns: 12rem minmax(0, 1fr);
      gap: var(--ddds-gap);
      align-items: start;
    }
    .ddds-tabs--vertical .ddds-tabs__strip {
      flex-direction: column;
      align-items: stretch;
      gap: 0.15rem;
      overflow-x: visible;
      margin-bottom: 0;
      border-bottom: 0;
      border-right: 1px solid var(--ddds-line);
      padding-right: 0.5rem;
    }
    /* The marker moves with the strip: a bottom rule under a stacked tab would read as a divider. */
    .ddds-tabs--vertical .ddds-tabs__tab {
      width: 100%;
      border-bottom: 0;
      border-left: 2px solid transparent;
      border-radius: var(--ddds-radius-sm);
    }
    .ddds-tabs--vertical .ddds-tabs__tab[aria-selected="true"] {
      background: var(--ddds-accent-quiet);
      border-left-color: var(--ddds-accent);
    }
  }

  /*
    The activity trail, capped and scrolled inside its own card.

    It is the one list here with no upper bound: nothing is ever deleted (R4) and every state change
    appends an event (R3), so an exchange a year old has hundreds and the panel beside it would be a
    thumbnail. The cap is in rem rather than pixels so it tracks the type size, and it is lower on a
    phone, where the page scrolls anyway and a nested scroller is easy to trap a thumb in.

    Newest first, so the cap hides history rather than the thing that just happened.
  */
  .ddds-activity {
    max-height: 22rem;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  @media (max-width: 60rem) { .ddds-activity { max-height: 16rem; } }

  /* ---------------------------------------------------------------------
     The application shell (DG.18)
     ---------------------------------------------------------------------
     Only the standalone application wears this. The widget must not: it is
     handed a panel inside a CRM that has its own navigation, and a second
     rail inside somebody else's application is the embedded-component
     mistake section 14 exists to avoid.
  */
  .ddds-app { display: grid; grid-template-columns: minmax(0, 1fr); min-height: 100vh; }
  @media (min-width: 60rem) {
    .ddds-app { grid-template-columns: 224px minmax(0, 1fr); }
  }

  /*
    Narrow first: the rail is a horizontal strip that scrolls, because a rep on a phone
    should not lose a third of the screen to navigation. It becomes a column at the same
    breakpoint the content does.
  */
  .ddds-rail {
    background: var(--ddds-rail);
    border-bottom: 1px solid var(--ddds-line);
    padding: 0.6rem 0.75rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.4rem;
    overflow-x: auto;
  }
  @media (min-width: 60rem) {
    .ddds-rail {
      flex-direction: column;
      align-items: stretch;
      gap: 0.15rem;
      overflow-x: visible;
      border-bottom: 0;
      border-right: 1px solid var(--ddds-line);
      padding: 1rem 0.75rem 0.75rem;
    }
  }

  .ddds-mark { display: flex; align-items: center; gap: 0.55rem; padding: 0 0.35rem; flex: none; }
  @media (min-width: 60rem) { .ddds-mark { padding: 0 0.35rem 1.1rem; } }
  .ddds-mark__glyph {
    width: 28px;
    height: 28px;
    flex: none;
    border-radius: var(--ddds-radius-sm);
    background: var(--ddds-accent);
    color: var(--ddds-accent-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--ddds-font-display);
    font-weight: 700;
    font-size: 0.92rem;
  }
  .ddds-mark__name {
    font-family: var(--ddds-font-display);
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }

  .ddds-rail__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    min-height: var(--ddds-touch);
    border: 0;
    border-radius: var(--ddds-radius-sm);
    background: transparent;
    font: inherit;
    font-size: 0.9rem;
    color: var(--ddds-ink);
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
    flex: none;
  }
  @media (min-width: 60rem) { .ddds-rail__item { width: 100%; } }
  .ddds-rail__item:hover { background: var(--ddds-accent-quiet); }
  .ddds-rail__item:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 2px; }
  .ddds-rail__item[aria-current="page"] {
    background: var(--ddds-accent-quiet);
    color: var(--ddds-accent);
    font-weight: 600;
  }

  .ddds-rail__heading {
    font-family: var(--ddds-font-mono);
    font-size: 0.62rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ddds-ink-faint);
    margin: 1.1rem 0 0.35rem;
    padding: 0 0.6rem;
  }
  /* The rail is a strip on a phone, where a section heading in the middle of it makes no sense. */
  .ddds-rail__heading { display: none; }
  @media (min-width: 60rem) { .ddds-rail__heading { display: block; } }

  .ddds-rail__spacer { display: none; }
  @media (min-width: 60rem) { .ddds-rail__spacer { display: block; flex: 1 1 auto; min-height: 1rem; } }

  .ddds-rail__foot {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-left: auto;
    flex: none;
  }
  @media (min-width: 60rem) {
    .ddds-rail__foot {
      margin-left: 0;
      padding-top: 0.5rem;
      border-top: 1px solid var(--ddds-line);
      flex-wrap: wrap;
    }
  }
  .ddds-rail__me { min-width: 0; display: none; }
  @media (min-width: 60rem) { .ddds-rail__me { display: block; } }
  .ddds-rail__name { display: block; font-size: 0.85rem; font-weight: 600; }
  .ddds-rail__role { display: block; font-size: 0.75rem; color: var(--ddds-ink-quiet); }

  .ddds-main { padding: 1.1rem 1rem 2.5rem; min-width: 0; }
  @media (min-width: 60rem) { .ddds-main { padding: 1.4rem 1.5rem 2.5rem; } }

  .ddds-crumb { font-size: 0.85rem; color: var(--ddds-ink-quiet); margin: 0 0 0.5rem; }
  .ddds-crumb__here { color: var(--ddds-ink); font-weight: 600; }

  .ddds-avatar {
    width: 26px;
    height: 26px;
    flex: none;
    border-radius: var(--ddds-radius-pill);
    background: var(--ddds-accent-quiet);
    color: var(--ddds-accent);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }
  .ddds-avatar--lg { width: 40px; height: 40px; font-size: 0.9rem; }

  /*
    A real table for the contact directory, and a real one deliberately.

    A directory is tabular data: a screen reader should get the column names with each cell, which is
    what a grid of divs cannot give it. It scrolls inside its own container rather than widening the
    page, because the alternative on a phone is a body that scrolls sideways.
  */
  /*
    Present for a screen reader, absent on the screen.

    The clipping form rather than display:none or visibility:hidden, both of which take the element out
    of the accessibility tree as well - which would defeat the point. Used for a column heading whose
    meaning is obvious from the button underneath it, and which would only add noise if drawn.
  */
  .ddds-visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  .ddds-table-scroll { overflow-x: auto; }
  .ddds-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  .ddds-table th {
    text-align: left;
    font-family: var(--ddds-font-mono);
    font-size: 0.64rem;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--ddds-ink-faint);
    font-weight: 400;
    padding: 0.5rem 0.7rem;
    border-bottom: 1px solid var(--ddds-line-strong);
    white-space: nowrap;
  }
  .ddds-table td { padding: 0.7rem; border-bottom: 1px solid var(--ddds-line); vertical-align: middle; }
  .ddds-table tr:last-child td { border-bottom: 0; }
  /* Dates and counts line up under each other, which is the whole reason to use a table. */
  .ddds-table__num { font-variant-numeric: tabular-nums; color: var(--ddds-ink-quiet); white-space: nowrap; }
  .ddds-table__who { display: flex; align-items: center; gap: 0.6rem; }
  .ddds-table__name { display: block; font-weight: 600; }
  .ddds-table__email { display: block; color: var(--ddds-ink-quiet); font-size: 0.82rem; overflow-wrap: anywhere; }

  /*
    The theme control: three states in one group, because "system" is a real answer and a
    two-state switch cannot express it. Segmented rather than a dropdown so the current
    state is legible without opening anything.
  */
  .ddds-theme { display: inline-flex; gap: 2px; padding: 2px; background: var(--ddds-sunken); border-radius: var(--ddds-radius-pill); }
  .ddds-theme__option {
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.75rem;
    color: var(--ddds-ink-quiet);
    padding: 0.25rem 0.6rem;
    border-radius: var(--ddds-radius-pill);
    cursor: pointer;
    line-height: 1.4;
  }
  .ddds-theme__option:hover { color: var(--ddds-ink); }
  .ddds-theme__option[aria-pressed="true"] {
    background: var(--ddds-surface);
    color: var(--ddds-ink);
    font-weight: 600;
    box-shadow: var(--ddds-shadow);
  }
  .ddds-theme__option:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 1px; }

  /* ---------------------------------------------------------------------
     The dialogs (DG.33)
     ---------------------------------------------------------------------
     Two shapes from one component, and the difference is geometry rather
     than taste. The widget's frame is sized to its own content, so its
     viewport *is* the whole frame: a centred fixed dialog in a 1200px frame
     lands 600px down, below the fold of whatever the CRM is showing. So the
     embedded shape is a takeover in normal flow - the panels are hidden,
     this takes their place, and the frame resizes to it.
  */
  .ddds-modal__scrim {
    position: fixed;
    inset: 0;
    z-index: 40;
    background: var(--ddds-scrim);
    display: grid;
    place-items: center;
    padding: var(--ddds-gap);
    /* The scrim scrolls, not the dialog's own body: a long staged list on a short phone would
       otherwise put the footer's buttons somewhere a thumb cannot reach. */
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  /*
    A real dialog element, so the UA stylesheet has opinions to undo.

    An open <dialog> is absolutely positioned against its containing block with auto margins,
    fit-content sizing, a solid border and 1em of padding. All of that is replaced here: the overlay
    is centred by the scrim's grid and the takeover sits in flow, which is the whole reason
    showModal() is not used.
  */
  .ddds-modal {
    position: static;
    inset: auto;
    margin: 0;
    padding: 0;
    color: inherit;
    width: min(32rem, 100%);
    background: var(--ddds-surface);
    border: 1px solid var(--ddds-line);
    border-radius: var(--ddds-radius);
    box-shadow: var(--ddds-shadow);
    display: flex;
    flex-direction: column;
    max-height: 100%;
  }
  .ddds-modal:focus { outline: none; }
  .ddds-modal:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 2px; }

  /*
    The same card, in flow rather than fixed, and uncapped in height on purpose: the frame's
    ResizeObserver reports this element's height to the host, so a scroller here would report a
    short frame with the footer hidden inside it.

    Still capped in width and still centred. A CRM's panel can be 900px wide and a form field
    stretched across all of it is a different, worse thing than the dialog this is meant to be.
  */
  .ddds-modal--takeover {
    width: 100%;
    max-width: 34rem;
    margin: 0 auto;
    max-height: none;
  }

  .ddds-modal__head {
    display: flex;
    align-items: center;
    gap: var(--ddds-gap);
    padding: var(--ddds-gap);
    border-bottom: 1px solid var(--ddds-line);
  }

  .ddds-modal__title {
    margin: 0;
    margin-right: auto;
    font-family: var(--ddds-font-display);
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .ddds-modal__close {
    border: 0;
    background: transparent;
    color: var(--ddds-ink-quiet);
    font: inherit;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    border-radius: var(--ddds-radius-sm);
    /* A full touch target even though the glyph is small. D-15 puts reps on phones. */
    min-width: var(--ddds-touch);
    min-height: var(--ddds-touch);
    display: grid;
    place-items: center;
  }
  .ddds-modal__close:hover { color: var(--ddds-ink); background: var(--ddds-sunken); }
  .ddds-modal__close:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 1px; }

  .ddds-modal__body { padding: var(--ddds-gap); overflow-y: auto; }
  /* No inner scroller when the frame is sizing itself to this; see .ddds-modal--takeover. */
  .ddds-modal--takeover .ddds-modal__body { overflow-y: visible; }

  .ddds-modal__foot {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    flex-wrap: wrap;
    padding: var(--ddds-gap);
    border-top: 1px solid var(--ddds-line);
  }
  /* On a phone the two actions are stacked full width, which is the same call the buttons make
     everywhere else: a 44px target beats a tidy row. */
  .ddds-modal__foot .ddds-button { flex: 1 1 auto; }
  @media (min-width: 30rem) {
    .ddds-modal__foot .ddds-button { flex: 0 0 auto; }
  }

  /* The label field and its Add button, side by side once there is room for them. */
  .ddds-modal__add { display: flex; flex-direction: column; gap: 0.5rem; }
  @media (min-width: 30rem) {
    .ddds-modal__add { flex-direction: row; align-items: flex-end; }
    .ddds-modal__add .ddds-field { flex: 1 1 auto; margin-bottom: 0; }
  }

  /*
    The drop zone is a button, which is why this resets one rather than decorating a div.

    A target you can drop on has to be operable without a pointer too, and a native control is the
    honest way to get that - so the whole zone is the button that opens the picker, and dragging onto
    it does the same job. It also keeps the activation the file chooser needs; see dialogs.tsx.
  */
  .ddds-drop {
    display: block;
    width: 100%;
    font: inherit;
    color: inherit;
    cursor: pointer;
    border: 2px dashed var(--ddds-line-strong);
    border-radius: var(--ddds-radius);
    background: var(--ddds-sunken);
    padding: calc(var(--ddds-gap) * 1.5) var(--ddds-gap);
    text-align: center;
  }
  .ddds-drop:hover { border-color: var(--ddds-accent); }
  .ddds-drop:focus-visible { outline: 2px solid var(--ddds-accent); outline-offset: 2px; }
  .ddds-drop--over { border-color: var(--ddds-accent); background: var(--ddds-accent-quiet); }
  .ddds-drop__lead { font-weight: 600; color: var(--ddds-accent); }
  .ddds-drop__hint { color: var(--ddds-ink-quiet); }

  /*
    The batch being sent, below the tab panel rather than inside a card (DG.41).

    It used to be .ddds-queue, inside "You sent" where the documents land. Only the selected panel is
    mounted now, so a queue nested in that card would unmount the moment a rep switched to Activity and
    take a transfer in progress off the screen with it.

    Sticky, and that pays off on the standalone page only. Inside the widget it computes as static: the
    frame is content-sized by iqz-ddds:resize, so it never scrolls and there is no scrollport for this to
    stick to. The property that matters there - nothing above it moves while the bytes go - comes from
    rendering below the panel rather than from sticky. Without this note somebody checks it in the CRM,
    sees a bar that does not stick, and reports it broken.
  */
  .ddds-rep-sending {
    position: sticky;
    bottom: 0;
    margin-top: var(--ddds-gap);
    border-top: 1px solid var(--ddds-line);
    background: var(--ddds-surface);
    padding: var(--ddds-gap) 0;
  }

  /*
    The panel, as one element a dialog can hide or make inert.

    Restated rather than left to the user agent on purpose. The [hidden] attribute is a UA rule at the
    lowest possible precedence, so any display declared on this element would beat it silently and the
    takeover would render the form on top of a full panel - which is exactly how a hidden attribute
    stopped working in democrm. The two-column grid therefore lives on a child, and this stays a plain
    block with nothing to override it. No backticks in comments here: this file is one template
    literal, and a backtick ends it.
  */
  .ddds-rep-body[hidden] { display: none; }
`;
